import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, TrainFront, Building2, CalendarClock, Ship, MoreHorizontal, AlertTriangle } from "lucide-react";
import { STATUS_TONE } from "./util";

const TABS = [
  ["all", "Tutti", (b) => !["Cancellata", "Preventivo"].includes(b.status)],
  ["todo", "Da effettuare", (b) => ["Confermata", "Assegnata"].includes(b.status)],
  ["run", "In corso", (b) => b.status === "In corso"],
  ["done", "Completati", (b) => b.status === "Completata"],
  ["cxl", "Annullati", (b) => b.status === "Cancellata"],
];
const iconFor = (b) => {
  const s = `${b.service_type || ""} ${b.pickup || ""} ${b.destination || ""}`.toLowerCase();
  if (/dispo/.test(s)) return CalendarClock;
  if (/fco|fiumicino|ciampino|aeroporto|airport|cia\b/.test(s)) return Plane;
  if (/stazione|termini|tiburtina|station/.test(s)) return TrainFront;
  if (/porto|civitavecchia/.test(s)) return Ship;
  return Building2;
};
const mins = (t) => { const [h, m] = (t || "").split(":").map(Number); return Number.isFinite(h) ? h * 60 + (m || 0) : null; };
const initials = (n) => n.split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase();

export function TodayBoard({ bookings, day, setDay, isToday, nowMin, names, conflicts }) {
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const rows = useMemo(() => bookings.filter(b => b.date === day).sort((a, b) => (a.time || "").localeCompare(b.time || "")), [bookings, day]);
  const list = rows.filter(TABS.find(t => t[0] === tab)[2]);
  const label = new Date(`${day}T12:00:00Z`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).replace(/(^|\s)\S/g, c => c.toUpperCase());
  return (
    <section className="mb-card p-4 md:p-5 min-w-0" data-testid="mb-today-board">
      <header className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="font-display text-xl text-white">{isToday ? "Servizi di oggi" : "Servizi del giorno"}</h2>
        <label className="relative px-3 h-8 rounded-full bg-white/[0.05] border border-white/10 text-[12px] text-slate-200 flex items-center cursor-pointer" data-testid="mb-today-date">
          {label}
          <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="mb-today-date-input" />
        </label>
        <button type="button" onClick={() => nav(`/prenotazioni?date=${day}`)} className="ml-auto h-8 px-4 rounded-lg border border-white/20 text-[12px] text-white hover:border-[#D4A72C]/60" data-testid="mb-today-all">Vedi tutti</button>
      </header>
      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="mb-today-tabs">
        {TABS.map(([id, l, f]) => (
          <button key={id} type="button" onClick={() => setTab(id)} data-testid={`mb-tab-${id}`}
            className={`h-8 px-3.5 rounded-full text-[12px] whitespace-nowrap transition-colors ${tab === id ? "bg-gradient-to-r from-[#B8892A] to-[#E9C46A] text-black font-semibold" : "text-slate-300 hover:bg-white/5"}`}>
            {l} <span className={`ml-1 text-[10.5px] ${tab === id ? "text-black/70" : "text-slate-500"}`}>{rows.filter(f).length}</span>
          </button>
        ))}
      </div>
      <div className="divide-y divide-white/[0.06] max-h-[420px] overflow-y-auto">
        {list.map(b => {
          const Icon = iconFor(b);
          const drv = names.driver(b.driver_id);
          const plate = names.plate(b.vehicle_id);
          const waiting = ["Confermata", "Assegnata"].includes(b.status);
          const m = mins(b.time);
          const soon = isToday && waiting && m != null && m - nowMin >= 0 && m - nowMin <= 60;
          return (
            <div key={b.id} role="button" tabIndex={0} onClick={() => nav(`/prenotazioni?open=${b.id}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/prenotazioni?open=${b.id}`)} data-testid={`mb-row-${b.id}`}
              className={`grid grid-cols-[52px_1fr_auto] md:grid-cols-[56px_22px_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.1fr)_110px_28px] items-center gap-x-3 gap-y-1 py-2.5 px-1 cursor-pointer hover:bg-white/[0.03] ${soon ? "border-l-2 border-l-[#E9C46A] pl-2" : ""}`}>
              <span className="font-mono-num text-white text-[15px]">{b.time || "--:--"}</span>
              <Icon size={16} className="hidden md:block text-slate-400" />
              <span className="min-w-0">
                <span className="block text-[13px] text-white truncate">{b.pickup || "—"} → {b.destination || "—"}</span>
                <span className="md:hidden block text-[11px] text-slate-400 truncate">{names.customer(b)} · {drv || "Senza autista"}{plate ? ` · ${plate}` : ""}</span>
                {soon && <span className="text-[10.5px] text-[#E9C46A]" data-testid={`mb-soon-${b.id}`}>Tra {m - nowMin} min</span>}
              </span>
              <span className="hidden md:block text-[12px] text-slate-400 truncate">{names.customer(b)}</span>
              <span className="hidden md:flex items-center gap-2 min-w-0">
                {drv ? <><span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E9C46A] to-[#8A6A1F] text-black text-[10px] font-bold flex items-center justify-center shrink-0">{initials(drv)}</span>
                  <span className="min-w-0 leading-tight"><span className="block text-[12.5px] text-white truncate">{drv}</span><span className="block text-[10.5px] text-slate-500 truncate">{plate || (b.partner_id ? "Fornitore" : "Senza veicolo")}</span></span></>
                  : <span className="text-[12px] text-amber-300 truncate" data-testid={`mb-unassigned-${b.id}`}>{b.partner_id ? "Affidato a fornitore" : "Senza autista"}</span>}
              </span>
              <span className="flex flex-col items-end md:items-stretch gap-1">
                <span className={`px-2 py-1 rounded-md border text-[11px] font-medium text-center whitespace-nowrap ${STATUS_TONE[b.status] || "bg-white/5 text-slate-300 border-white/10"}`}>{b.status}</span>
                {conflicts.has(b.id) && <span className="text-[10px] text-rose-300 inline-flex items-center gap-1" data-testid={`mb-conflict-${b.id}`}><AlertTriangle size={10} />Conflitto</span>}
              </span>
              <MoreHorizontal size={16} className="hidden md:block text-slate-500" />
            </div>
          );
        })}
        {!list.length && <div className="py-10 text-center text-sm text-slate-500" data-testid="mb-today-empty">{isToday && tab === "all" ? "Nessun servizio programmato per oggi" : "Nessun servizio in questa vista"}</div>}
      </div>
    </section>
  );
}
